import React, { useCallback, useEffect, useState } from 'react'

import { RestaurantIcon, WarningOutlineIcon } from '@toasttab/buffet-pui-icons'
import { ButtonType } from 'src/apollo/sites'
import { BrandedButton } from 'src/shared/components/common/button'
import { formatAddress } from 'src/shared/components/common/form_input/LocationInput'
import { ToggleInput } from 'src/shared/components/common/forms'
import {
  Modal,
  ModalContent,
  ModalOverlay
} from 'src/shared/components/common/modal/Modal'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useOOLocations } from 'public/components/online_ordering/FulfillmentContext'
import { useLocationSorting } from 'shared/js/hooks/useLocationSorting'
import { t } from 'src/translations'
import { SiteRestaurantLocation } from 'src/shared/js/types'
import { MenuItem } from 'public/components/default_template/menu_section/MenuSection'
import { PriceLine } from 'public/components/default_template/menu_section/MenuItemCard'
import { LocationStockStatus } from './LocationStockStatus'
import { LocationOptionLabel } from './LocationOptionLabel'

export const ChangeLocationConfirmationModal = ({
  selectedLocation,
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  headerText = t('confirm-location-modal.switch-location')
}: {
  selectedLocation: SiteRestaurantLocation
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  onCancel: () => void
  headerText?: string
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId='change-location-confirmation'
    >
      <ModalOverlay />
      <ModalContent contentClassName='modalContentV2 fulfillmentModal changeLocationModal confirmLocationChange bottomDrawerMobile'>
        <div className='modalContent'>
          <div className='topContent'>
            <RestaurantIcon
              accessibility='decorative'
              size='lg'
              className='rxIcon'
            />
            <div className='newLocation gap-8'>
              <h3 className='headline'>{headerText}</h3>
              <span className='locationAddress'>{selectedLocation.name}</span>
              <span className='locationAddress'>
                {formatAddress(selectedLocation)}
              </span>
            </div>
          </div>
          <div className='footer gap-8'>
            <BrandedButton variant={ButtonType.Primary} onClick={onConfirm}>
              {t('confirm-location-modal.button.confirm')}
            </BrandedButton>
            <BrandedButton
              variant={ButtonType.Inverted}
              onClick={onCancel}
              className='cancelButton'
            >
              {t('confirm-location-modal.button.cancel')}
            </BrandedButton>
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}

const ChangeLocationModal = ({
  isOpen,
  onClose,
  onConfirmClose = () => {},
  menuItem
}: {
  isOpen: boolean
  onClose: () => void
  onConfirmClose?: () => void
  menuItem?: MenuItem
}) => {
  const { selectedLocation, updateLocation } = useRestaurant()
  const { cart } = useCart()
  const [draftSelectedLocation, setDraftSelectedLocation] = useState<string>(
    selectedLocation.externalId
  )
  const [updatedLocation, setUpdatedLocation] = useState(selectedLocation)
  const [confirmLocationChange, setConfirmLocationChange] = useState(false)
  const ooLocations = useOOLocations()
  const { sortedLocations, requestUserLocation, userLocation } =
    useLocationSorting(ooLocations)
  const { track } = useThrottledTracker()

  // Request user location when modal opens
  useEffect(() => {
    if (isOpen) {
      requestUserLocation()
    }
  }, [isOpen, requestUserLocation])

  const shouldDisplayMultiLocationStock =
    !!menuItem && (menuItem?.stockAcrossLocations?.length ?? 0) > 1

  const onCancel = useCallback(() => {
    setDraftSelectedLocation(selectedLocation.externalId)
    setConfirmLocationChange(false)
    onConfirmClose()
    onClose()
  }, [selectedLocation, onClose, onConfirmClose])

  const onConfirm = useCallback(() => {
    if (draftSelectedLocation !== selectedLocation.externalId) {
      track(TrackingEvents.LOCATION_CHANGE_CONFIRMED, {
        previousRestaurantGuid: selectedLocation.externalId,
        newRestaurantGuid: draftSelectedLocation,
        currentCartItemCount: cart?.order?.numberOfSelections || 0
      })
      updateLocation(draftSelectedLocation)
      setConfirmLocationChange(true)
      onConfirmClose()
      onClose()
    } else {
      onConfirmClose()
      onClose()
    }
  }, [
    draftSelectedLocation,
    selectedLocation,
    updateLocation,
    onClose,
    track,
    cart,
    onConfirmClose
  ])

  if (!ooLocations?.length || ooLocations.length === 1) {
    return null
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} testId='change-location-modal'>
        <ModalOverlay />
        <ModalContent contentClassName='modalContentV2 fullScreenMobile fulfillmentModal changeLocationModal'>
          <div className='modalHeader'>
            <h2 id='change-location-modal-heading'>
              {shouldDisplayMultiLocationStock
                ? t('product-display.change-location-modal.header')
                : t('change-location-modal.header')}
            </h2>
          </div>
          {(!!cart || shouldDisplayMultiLocationStock) && (
            <div className='warningBanner'>
              <WarningOutlineIcon size='xs' accessibility='decorative' />
              <span>{t('change-location-modal.cart-warning')}</span>
            </div>
          )}

          <div className='modalContent'>
            {!shouldDisplayMultiLocationStock ? (
              <div className='currentLocation'>
                <h3>{t('change-location-modal.current-location')}</h3>
                <div className='locationDetails'>
                  <span className='address'>
                    <RestaurantIcon
                      accessibility='decorative'
                      className='rxIcon'
                    />
                    <div className='locationLabel'>
                      <span className='locationName'>
                        {selectedLocation.name}
                      </span>
                      <span className='locationAddress'>
                        {formatAddress(selectedLocation)}
                      </span>
                    </div>
                  </span>
                </div>
              </div>
            ) : (
              <div className='menuItemCard'>
                <div className='menuItemInformation'>
                  <h3 className='menuItemName'>{menuItem.name}</h3>
                  <p className='menuItemDescription'>{menuItem.description}</p>
                  <span className='menuItemPrice'>
                    <PriceLine item={menuItem} />
                  </span>
                </div>
                {menuItem.imageUrls?.small && (
                  <div className='menuItemImageContainer'>
                    <img
                      src={menuItem.imageUrls.small}
                      alt={menuItem.name || ''}
                      className='menuItemImage'
                    />
                  </div>
                )}
              </div>
            )}
            <div
              className='otherLocations'
              role='radiogroup'
              aria-labelledby={
                shouldDisplayMultiLocationStock
                  ? 'change-location-modal-heading'
                  : 'change-location-other-locations-heading'
              }
            >
              {!shouldDisplayMultiLocationStock && (
                <h3 id='change-location-other-locations-heading'>
                  {t('change-location-modal.other-locations')}
                </h3>
              )}

              <ul className='radioButtons'>
                {sortedLocations
                  .filter(
                    (location) =>
                      location.externalId !== selectedLocation?.externalId
                  )
                  .filter(
                    (location) =>
                      !shouldDisplayMultiLocationStock ||
                      menuItem?.stockAcrossLocations?.some(
                        (stock) =>
                          stock.restaurantGuid === location.externalId &&
                          !stock.outOfStock
                      )
                  )
                  .map((location) => (
                    <li key={location.externalId} className='radioButtonOption'>
                      <ToggleInput
                        checked={draftSelectedLocation === location.externalId}
                        onChange={() => {
                          setUpdatedLocation(location)
                          setDraftSelectedLocation(location.externalId)
                        }}
                        name='changeLocation'
                        id={location.externalId}
                        type='radio'
                        dataTestId={`input-location-${location.name}`}
                      >
                        <LocationOptionLabel
                          location={location}
                          showDistance={!!userLocation}
                          additionalInfo={
                            shouldDisplayMultiLocationStock ? (
                              <LocationStockStatus
                                restaurantGuid={location.externalId}
                                stockData={menuItem?.stockAcrossLocations!}
                              />
                            ) : undefined
                          }
                        />
                      </ToggleInput>
                    </li>
                  ))}
              </ul>
            </div>
          </div>

          <div className='footer'>
            <BrandedButton
              variant={ButtonType.Inverted}
              onClick={onCancel}
              className='cancelButton'
            >
              {t('change-location-modal.button.cancel')}
            </BrandedButton>
            <BrandedButton
              variant={ButtonType.Primary}
              onClick={() => {
                setConfirmLocationChange(true)
                onClose()
              }}
              disabled={draftSelectedLocation === selectedLocation.externalId}
            >
              {t('change-location-modal.button.continue')}
            </BrandedButton>
          </div>
        </ModalContent>
      </Modal>
      <ChangeLocationConfirmationModal
        isOpen={confirmLocationChange}
        onClose={() => {
          setConfirmLocationChange(false)
          onClose()
        }}
        selectedLocation={updatedLocation}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </>
  )
}

export default ChangeLocationModal
